import { ChevronDown } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Country, defaultCountries } from "../utils/countryUtils";

/**
 * Props for the CountrySelect component
 * @interface CountrySelectProps
 * @property {Country} selected - Currently selected country
 * @property {(country: Country) => void} onSelect - Callback when a country is selected
 * @property {(e: React.FocusEvent<HTMLButtonElement>) => void} onFocus - Callback when the button is focused
 * @property {string} variant - The variant of the component
 * @property {string} className - Additional class names for the component
 */
interface CountrySelectProps {
  selected: Country;
  onSelect: (country: Country) => void;
  onFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  variant?: "default" | "form";
  className?: string;
}

/**
 * A dropdown component for selecting a country with flag emojis and country codes.
 * Features a clickaway listener and keyboard navigation support.
 *
 * @component
 * @example
 * ```tsx
 * <CountrySelect
 *   selected={defaultCountry}
 *   onSelect={(country) => {
 *     console.log(`Selected country: ${country.name}`);
 *   }}
 * />
 * ```
 */
export function CountrySelect({
  selected,
  onSelect,
  onFocus,
  variant = "default",
  className = "",
}: CountrySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const buttonStyles = {
    default:
      "flex items-center h-full justify-between w-full px-4 py-3 text-left bg-gray-100 rounded-l-lg focus:outline-none",
    form: "flex items-center justify-between w-full px-3 py-2 text-left border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500",
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onFocus={onFocus}
        className={`${buttonStyles[variant]} ${isOpen && variant === "form" ? "border-primary-500 ring-2 ring-primary-500/20" : ""}`}
      >
        <div className="flex items-center">
          <span className="text-2xl mr-3">{selected.flag}</span>
          <span
            className={`font-medium ${variant === "form" ? "text-gray-800 text-base" : "text-gray-500 text-base"}`}
          >
            {selected.code}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 ${variant === "form" ? "text-gray-500" : "text-gray-400"} ml-2`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 w-full bg-white rounded-md shadow-lg z-[100] border border-gray-200">
          <ul className="py-1 max-h-60 overflow-auto">
            {defaultCountries.map((country) => (
              <li key={country.code}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(country);
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-3 py-2 text-sm hover:bg-gray-50"
                >
                  <span className="text-2xl mr-3">{country.flag}</span>
                  <span className="font-medium text-gray-800 text-base">
                    {country.code}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
